import { Clock, User, AlertCircle, Check, Coffee, X, Edit } from 'lucide-react';
import { Card, StatusBadge, Button } from '@components/ui';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { calismaDurumlari } from '@mocks/puantaj';

/**
 * PuantajTablosu - Günlük puantaj listesi tablosu
 */
export default function PuantajTablosu({ data, onEdit, onDurumChange }) {
    const getDurumBadge = (durum) => {
        const d = calismaDurumlari.find(c => c.key === durum);
        return d || { key: durum, label: durum, color: 'neutral' };
    };

    const formatSaat = (saat) => {
        if (!saat) return 0;
        return typeof saat === 'number' ? saat.toFixed(1) : saat;
    };

    // Mobile Card View
    const MobileCard = ({ kayit }) => {
        const durum = getDurumBadge(kayit.durum);
        return (
            <Card padding="none" className="md:hidden">
                <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                                <User size={20} className="text-secondary" />
                            </div>
                            <div>
                                <Link to={`/insaat/personel/${kayit.personelId}`} className="font-medium text-text-primary hover:text-primary transition-colors hover:underline">
                                    {kayit.personelAdi}
                                </Link>
                                <p className="text-xs text-text-muted">{kayit.gorev}</p>
                            </div>
                        </div>
                        <StatusBadge variant={durum.color} status={durum.label} />
                    </div>

                    {kayit.durum === 'calisti' || kayit.durum === 'yarim_gun' ? (
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="bg-bg-tertiary rounded-lg p-2">
                                <Clock size={14} className="mx-auto text-text-muted mb-1" />
                                <p className="text-sm font-medium text-text-primary">{kayit.baslangic} - {kayit.bitis}</p>
                                <p className="text-xs text-text-muted">Mesai</p>
                            </div>
                            <div className="bg-bg-tertiary rounded-lg p-2">
                                <p className="text-lg font-bold text-text-primary">{formatSaat(kayit.calismaSaat)}</p>
                                <p className="text-xs text-text-muted">Saat</p>
                            </div>
                            <div className="bg-bg-tertiary rounded-lg p-2">
                                <p className={clsx("text-lg font-bold", kayit.fazlaMesai > 0 ? "text-warning" : "text-text-muted")}>
                                    +{formatSaat(kayit.fazlaMesai)}
                                </p>
                                <p className="text-xs text-text-muted">Fazla Mesai</p>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center text-text-muted py-4">
                            {kayit.not || 'Bugün çalışmadı'}
                        </div>
                    )}

                    {kayit.not && kayit.durum === 'calisti' && (
                        <p className="text-xs text-text-tertiary mt-3 italic">Not: {kayit.not}</p>
                    )}
                </div>
            </Card>
        );
    };

    return (
        <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
                {data.map((kayit) => (
                    <MobileCard key={kayit.id} kayit={kayit} />
                ))}
            </div>

            {/* Desktop Table */}
            <Card padding="none" className="hidden md:block overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-bg-tertiary border-b border-border-light">
                                <th className="text-left py-3 px-4 text-xs font-semibold text-text-secondary">Personel</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-text-secondary">Görev</th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-text-secondary">Mesai</th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-text-secondary">Çalışma</th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-text-secondary">F.Mesai</th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-text-secondary">Durum</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-text-secondary">Not</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-text-secondary">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((kayit, idx) => {
                                const durum = getDurumBadge(kayit.durum);
                                return (
                                    <tr
                                        key={kayit.id}
                                        className={clsx(
                                            "border-b border-border-light hover:bg-bg-hover transition-colors",
                                            idx % 2 === 0 ? "bg-bg-secondary" : "bg-bg-primary"
                                        )}
                                    >
                                        <td className="py-3 px-4">
                                            <Link
                                                to={`/insaat/personel/${kayit.personelId}`}
                                                className="font-medium text-text-primary hover:text-primary transition-colors hover:underline"
                                            >
                                                {kayit.personelAdi}
                                            </Link>
                                        </td>
                                        <td className="py-3 px-4 text-sm text-text-secondary">{kayit.gorev}</td>
                                        <td className="py-3 px-4 text-center text-sm text-text-secondary">
                                            {kayit.baslangic && kayit.bitis ? `${kayit.baslangic} - ${kayit.bitis}` : '-'}
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <span className="font-medium text-text-primary">{formatSaat(kayit.calismaSaat)}h</span>
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <span className={clsx(
                                                "font-medium",
                                                kayit.fazlaMesai > 0 ? "text-warning" : "text-text-muted"
                                            )}>
                                                {kayit.fazlaMesai > 0 ? `+${formatSaat(kayit.fazlaMesai)}h` : '-'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <StatusBadge variant={durum.color} status={durum.label} />
                                        </td>
                                        <td className="py-3 px-4 text-sm text-text-muted max-w-[200px] truncate">
                                            {kayit.not || '-'}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <Button size="sm" variant="ghost" onClick={() => onEdit?.(kayit)}>
                                                <Edit size={16} />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );
}
